/**
 * Utility functions for phone number handling and conversion
 * Consolidated phone utilities for the entire application
 */
import { ActionType } from '../models/action-type.js';
import { parseTransferActionParameters, transferPhoneRows, } from '../models/transfer-action.js';
/**
 * Phone number constants for US/Canada
 */
export const PHONE_CONSTANTS = {
    US_COUNTRY_CODE: 'US',
    US_CALLING_CODE: '+1',
    US_E164_PREFIX: '+1',
    US_E164_LENGTH: 12, // +1 plus 10 digits
    US_NATIONAL_LENGTH: 10,
    CANADA_COUNTRY_CODE: 'CA',
};
/**
 * Parse an E.164 formatted phone number into components for TwentyCRM
 * Currently supports US/Canada numbers (+1) only
 *
 * @param e164Phone - Phone number in E.164 format (e.g., "+14065102770")
 * @returns Phone components or null if invalid
 */
export function parseE164ToComponents(e164Phone) {
    if (!e164Phone.startsWith(PHONE_CONSTANTS.US_E164_PREFIX)) {
        console.warn(`Unsupported phone number format: ${e164Phone}. Currently only US/Canada (${PHONE_CONSTANTS.US_CALLING_CODE}) numbers are supported.`);
        return null;
    }
    const usPhoneRegex = /^\+1(\d{10})$/;
    const match = e164Phone.match(usPhoneRegex);
    if (!match) {
        return null;
    }
    return {
        phoneNumber: match[1],
        countryCode: PHONE_CONSTANTS.US_COUNTRY_CODE,
        callingCode: PHONE_CONSTANTS.US_CALLING_CODE,
    };
}
/**
 * Convert phone components back to E.164 format
 * @param components - Phone components from TwentyCRM
 * @returns E.164 formatted phone number
 */
export function componentsToE164(components) {
    const callingCodeDigits = components.callingCode.replace('+', '');
    return `+${callingCodeDigits}${components.phoneNumber}`;
}
/**
 * Strip US country code from E.164 phone number when it's a valid US/Canada number.
 * For non-US numbers, returns the input unchanged (passthrough).
 *
 * @param e164Phone - Phone number in E.164 format
 * @returns 10-digit US phone number without country code, or original string for non-US numbers
 */
export function stripUSPrefixIfUS(e164Phone) {
    if (e164Phone.startsWith(PHONE_CONSTANTS.US_E164_PREFIX) &&
        e164Phone.length === PHONE_CONSTANTS.US_E164_LENGTH) {
        return e164Phone.substring(2);
    }
    return e164Phone;
}
/**
 * Check if a phone number is a US/Canada number (starts with +1 and correct length)
 * @param e164Phone - Phone number in E.164 format
 * @returns True if US/Canada number
 */
export function isUSPhoneNumber(e164Phone) {
    if (!e164Phone)
        return false;
    return (e164Phone.startsWith(PHONE_CONSTANTS.US_E164_PREFIX) &&
        e164Phone.length === PHONE_CONSTANTS.US_E164_LENGTH);
}
/**
 * Validate a full E.164 number: '+', a 1–9 country-code lead, then 6–14 more
 * digits (7–15 total). Rejects malformed values such as leading-zero country
 * codes (e.g. +0000000000) and implausibly short numbers.
 */
export function isValidE164Number(phone) {
    if (!phone)
        return false;
    return /^\+[1-9]\d{6,14}$/.test(phone.trim());
}
/**
 * Prepare phone data for TwentyCRM format
 * For US numbers: strips +1 and returns calling code
 * For international numbers: returns full E.164 number without calling code (let CRM infer)
 *
 * @param e164Phone - Phone number in E.164 format (e.g., "+15551234567" or "+447911123456")
 * @returns Object with phoneNumber and optional callingCode, or null if invalid
 */
export function preparePhoneForCRM(e164Phone) {
    if (!e164Phone)
        return null;
    // Strip a trailing #extension and trim once. The CRM phone model has no
    // extension field, so keep the callable base number instead of dropping the
    // whole phone; trimming keeps validation and the value sent to the CRM in sync.
    const phone = e164Phone.split('#')[0].trim();
    if (!phone)
        return null;
    if (isUSPhoneNumber(phone)) {
        // US number: strip +1 and provide explicit calling code
        return {
            phoneNumber: stripUSPrefixIfUS(phone),
            callingCode: PHONE_CONSTANTS.US_CALLING_CODE,
        };
    }
    // International number: reject malformed values (e.g. leading-zero country
    // codes like +0000000000) before handing off, since one bad value can fail a
    // whole bulk CRM batch. Genuine international numbers (e.g. +447911123456) pass.
    if (!isValidE164Number(phone)) {
        return null;
    }
    // Pass full E.164 number, let TwentyCRM infer calling code.
    // Don't set callingCode to avoid conflicts
    return {
        phoneNumber: phone,
        callingCode: undefined,
    };
}
/**
 * Format US phone number for display
 * @param e164Phone - Phone number in E.164 format
 * @returns Formatted phone number for display
 */
export function formatUSPhoneForDisplay(e164Phone) {
    if (e164Phone.startsWith(PHONE_CONSTANTS.US_E164_PREFIX) &&
        e164Phone.length === PHONE_CONSTANTS.US_E164_LENGTH) {
        const areaCode = e164Phone.substring(2, 5);
        const exchange = e164Phone.substring(5, 8);
        const number = e164Phone.substring(8, 12);
        return `(${areaCode}) ${exchange}-${number}`;
    }
    return e164Phone;
}
/**
 * Format a stored or user-typed phone for display, preserving an optional
 * `#extension`. Composes `extractPhoneAndExtension` + `formatUSPhoneForDisplay`
 * so the two base helpers stay single-purpose.
 *
 *   "+17082035932"       → "+1 (708) 203-5932"
 *   "+17082035932#23"    → "+1 (708) 203-5932 #23"
 *   "+447700900000"      → "+447700900000"   (non-US passthrough)
 *   ""                   → ""
 */
export function formatPhoneForDisplay(raw) {
    if (!raw)
        return '';
    const { phoneNumber, extension } = extractPhoneAndExtension(raw);
    const base = isUSPhoneNumber(phoneNumber)
        ? `+1 ${formatUSPhoneForDisplay(phoneNumber)}`
        : phoneNumber;
    return extension ? `${base} #${extension}` : base;
}
/**
 * Format a phone number for a form field in an integration (for example the
 * AppFolio or Yardi Breeze guest card form).
 *
 * A US number becomes `(816) 762-8330` (see `formatUSPhoneForDisplay`).
 * A non-US number stays in E.164 format, but only if the value already
 * starts with `+`. A short number such as `845-3259` is not a full phone
 * number. `normalizePhoneNumber` changes it to `+8453259`, which looks like a
 * non-US number but is not valid, so this function gives undefined.
 *
 *   "+18167628330"   → "(816) 762-8330"
 *   "816-762-8330"   → "(816) 762-8330"
 *   "+447700900000"  → "+447700900000"
 *   "845-3259"       → undefined
 *
 * @returns the formatted number, or undefined if the value is not a phone number
 */
export function formatPhoneForForm(value) {
    const e164 = normalizePhoneNumber(value ?? undefined);
    if (!e164)
        return undefined;
    const formatted = formatUSPhoneForDisplay(e164);
    if (formatted !== e164)
        return formatted;
    return value?.trim().startsWith('+') ? e164 : undefined;
}
/**
 * Normalize phone number to E.164 format (+18885551234)
 * Supports international numbers and US/Canada numbers
 *
 * @param phoneInput - Raw phone input from CSV or user input
 * @returns Normalized phone number or undefined if invalid
 */
export function normalizePhoneNumber(phoneInput) {
    if (!phoneInput?.trim())
        return undefined;
    const trimmed = phoneInput.trim();
    // Extract extension (#N) before processing so it's preserved across all branches
    const hashIdx = trimmed.indexOf('#');
    const hasNumericExt = hashIdx >= 0 && /^\d+$/.test(trimmed.substring(hashIdx + 1));
    const baseInput = hasNumericExt ? trimmed.substring(0, hashIdx) : trimmed;
    const ext = hasNumericExt ? trimmed.substring(hashIdx) : '';
    // If already in E.164 format (starts with + followed by digits), validate and return
    if (baseInput.startsWith('+')) {
        const digitsAfterPlus = baseInput.substring(1).replace(/\D/g, '');
        // E.164 numbers should be 7-15 digits (excluding the +)
        if (digitsAfterPlus.length >= 7 && digitsAfterPlus.length <= 15) {
            return `+${digitsAfterPlus}${ext}`;
        }
        return undefined;
    }
    // Remove all non-digits for numbers without +
    const digits = baseInput.replace(/\D/g, '');
    // Handle US/Canada formats (assume US if 10 or 11 digits starting with 1)
    if (digits.length === PHONE_CONSTANTS.US_NATIONAL_LENGTH) {
        // Format: 8885551234 (assume US)
        const areaCode = digits.substring(0, 3);
        if (isValidUSAreaCode(areaCode)) {
            return `${PHONE_CONSTANTS.US_E164_PREFIX}${digits}${ext}`;
        }
    }
    else if (digits.length === 11 && digits.startsWith('1')) {
        // Format: 18885551234 (US with country code)
        const nationalNumber = digits.substring(1);
        const areaCode = nationalNumber.substring(0, 3);
        if (isValidUSAreaCode(areaCode)) {
            return `${PHONE_CONSTANTS.US_E164_PREFIX}${nationalNumber}${ext}`;
        }
    }
    // For other formats with enough digits, assume it might be international
    // E.164 allows 7-15 digits total (country code + national number)
    if (digits.length >= 7 && digits.length <= 15) {
        return `+${digits}${ext}`;
    }
    return undefined;
}
/**
 * Normalize phone number to E.164 format - US/Canada ONLY
 * Rejects international numbers. Use this for US-specific contexts like CSV uploads.
 *
 * @param phoneInput - Raw phone input from CSV or user input
 * @returns Normalized US phone number or undefined if invalid/international
 */
export function normalizeUSPhoneNumber(phoneInput) {
    if (!phoneInput?.trim())
        return undefined;
    const trimmed = phoneInput.trim();
    // If starts with +, only accept +1 (US/Canada)
    if (trimmed.startsWith('+')) {
        // Check if it's a US number (+1)
        const digitsAfterPlus = trimmed.substring(1).replace(/\D/g, '');
        if (digitsAfterPlus.length === 11 && digitsAfterPlus.startsWith('1')) {
            const nationalNumber = digitsAfterPlus.substring(1);
            const areaCode = nationalNumber.substring(0, 3);
            if (isValidUSAreaCode(areaCode)) {
                return `${PHONE_CONSTANTS.US_E164_PREFIX}${nationalNumber}`;
            }
        }
        // Reject all other international formats
        return undefined;
    }
    // Remove all non-digits
    const digits = trimmed.replace(/\D/g, '');
    // Handle different input formats
    let nationalNumber;
    if (digits.length === PHONE_CONSTANTS.US_NATIONAL_LENGTH) {
        // Format: 8885551234 (assume US)
        nationalNumber = digits;
    }
    else if (digits.length === 11 && digits.startsWith('1')) {
        // Format: 18885551234 (US with country code)
        nationalNumber = digits.substring(1);
    }
    else {
        return undefined;
    }
    // Validate US/Canada area codes (first digit 2-9, second digit 0-9, third digit 0-9)
    if (nationalNumber.length !== PHONE_CONSTANTS.US_NATIONAL_LENGTH)
        return undefined;
    const areaCode = nationalNumber.substring(0, 3);
    if (!isValidUSAreaCode(areaCode))
        return undefined;
    // Return E.164 format
    return `${PHONE_CONSTANTS.US_E164_PREFIX}${nationalNumber}`;
}
/**
 * Validate US/Canada area code
 * @param areaCode - Area code to validate
 * @returns True if valid, false otherwise
 */
function isValidUSAreaCode(areaCode) {
    if (areaCode.length !== 3)
        return false;
    const firstDigit = parseInt(areaCode[0]);
    const secondDigit = parseInt(areaCode[1]);
    const thirdDigit = parseInt(areaCode[2]);
    // First digit must be 2-9
    if (firstDigit < 2 || firstDigit > 9)
        return false;
    // Second and third digits must be 0-9
    if (isNaN(secondDigit) || isNaN(thirdDigit))
        return false;
    return true;
}
/**
 * Extract phone number and extension from a phone string and normalize to E.164 format
 * Supports formats: "555-123-4567#23", "555-123-4567", "+15551234567#103", etc.
 *
 * @param phoneString - Phone string with optional extension
 * @returns Object with phoneNumber in E.164 format and optional extension
 *
 * @example
 * extractPhoneAndExtension("555-123-4567#23")
 * // Returns: { phoneNumber: "+15551234567", extension: "23" }
 *
 * extractPhoneAndExtension("555-123-4567")
 * // Returns: { phoneNumber: "+15551234567" }
 *
 * extractPhoneAndExtension("+15551234567#123")
 * // Returns: { phoneNumber: "+15551234567", extension: "123" }
 */
export function extractPhoneAndExtension(phoneString) {
    if (!phoneString || typeof phoneString !== 'string') {
        return { phoneNumber: '' };
    }
    const trimmed = phoneString.trim();
    // Pattern for extension: phone#123 (allowing whitespace around #)
    const extensionPattern = /^(.+?)\s*#\s*(\d+)$/;
    const match = trimmed.match(extensionPattern);
    let rawPhoneNumber;
    let extension;
    if (match) {
        rawPhoneNumber = match[1].trim();
        extension = match[2].trim();
    }
    else {
        rawPhoneNumber = trimmed;
    }
    // Normalize the phone number to E.164 format
    const normalizedPhone = normalizePhoneNumber(rawPhoneNumber);
    return {
        phoneNumber: normalizedPhone || rawPhoneNumber,
        extension,
    };
}
/**
 * Extract valid phone numbers from phone call actions
 * Filters out invalid/empty numbers and extracts just the phone portion (without extensions)
 *
 * @param phoneCallActions - Array of workflow action data containing phone numbers
 * @returns Array of valid phone numbers
 */
export function extractPhoneNumbers(phoneCallActions) {
    return phoneCallActions
        .map(action => {
        const numberParam = action.parameters?.number;
        if (!numberParam || typeof numberParam !== 'string')
            return null;
        // Extract just the phone number part (before any extension separator)
        const { phoneNumber } = extractPhoneAndExtension(numberParam);
        return phoneNumber;
    })
        .filter((number) => number !== null && typeof number === 'string' && number.length > 0);
}
/**
 * Extract phone number from various field types
 * Handles both string phone numbers and objects with phoneNumber property
 *
 * @param phoneField - Phone field that can be string or object with phoneNumber
 * @returns Phone number string or null if not found
 */
export function extractPhoneNumber(phoneField) {
    if (!phoneField)
        return null;
    if (typeof phoneField === 'string') {
        return phoneField;
    }
    if (typeof phoneField === 'object' && phoneField.phoneNumber) {
        return phoneField.phoneNumber;
    }
    return null;
}
/**
 * Extract phone numbers from workflow action groups.
 * Aggregates PHONE_CALL actions from all action groups into a flat, deduplicated array.
 *
 * @param workflow - Workflow with action groups
 * @returns Array of phone numbers from PHONE_CALL actions
 */
export function extractPhoneTreeFromWorkflow(workflow) {
    const phoneNumbers = [];
    if (!workflow.actionGroups) {
        return phoneNumbers;
    }
    for (const actionGroup of workflow.actionGroups) {
        if (!actionGroup.actions)
            continue;
        // A converted group dials its TRANSFER entries, as at dispatch. The
        // PHONE_CALL rows are a copy of them.
        const transfer = parseTransferActionParameters(actionGroup.actions.find(action => action.actionType === ActionType.TRANSFER));
        const numbers = transfer
            ? transferPhoneRows(transfer).map(row => row.parameters.number)
            : actionGroup.actions
                .filter(action => action.actionType === ActionType.PHONE_CALL)
                .map(action => action.parameters?.number);
        for (const phoneNumber of numbers) {
            if (phoneNumber && !phoneNumbers.includes(phoneNumber)) {
                phoneNumbers.push(phoneNumber);
            }
        }
    }
    return phoneNumbers;
}
/**
 * Extract first phone number from multiple workflows.
 * Returns first phone found (from first workflow's tree).
 *
 * @param workflows - Array of workflows with action groups
 * @returns First phone number or null
 */
export function extractFirstPhoneFromWorkflows(workflows) {
    for (const workflow of workflows) {
        const phoneTree = extractPhoneTreeFromWorkflow(workflow);
        if (phoneTree.length > 0)
            return phoneTree[0];
    }
    return null;
}
/**
 * Mask all but the last 4 digits of a phone number for privacy
 * Replaces all digits (except the last 4) with asterisks while preserving formatting
 *
 * @param phone - Phone number in any format (e.g., "+15551234567", "(555) 123-4567")
 * @returns Masked phone number (e.g., "+*******4567", "(**) ***-4567")
 *
 * @example
 * maskPhoneNumber("+15551234567")
 * // Returns: "+*******4567"
 *
 * maskPhoneNumber("(555) 123-4567")
 * // Returns: "(**) ***-4567"
 *
 * maskPhoneNumber("123")
 * // Returns: "***" (if less than 4 digits, mask all)
 */
export function maskPhoneNumber(phone) {
    if (!phone)
        return phone;
    // Get the last 4 characters
    const last4 = phone.slice(-4);
    // Get everything except the last 4
    const prefix = phone.slice(0, -4);
    // Replace all digits in the prefix with asterisks, preserving non-digit characters
    const maskedPrefix = prefix.replace(/\d/g, '*');
    return maskedPrefix + last4;
}
//# sourceMappingURL=phone-utils.js.map